import AuthenticatedRoute from '~/components/AuthenticatedRoute/AuthenticatedRoute';
import InvoiceDisplay from '~/pages/Settings/Account/Billing/Invoices/InvoiceDisplay';
import * as React from 'react';
import { matchPath, Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import { GlobalComponentsProvider } from '~/contexts/GlobalComponentsProvider';
import { useOnboarding2024 } from './pages/Onboarding2024/hooks/useOnboarding2024';

const Register = React.lazy(() => import('~/pages/Auth/Register'));
const Register2024 = React.lazy(() => import('~/pages/Auth/Register2024'));

const Main = React.lazy(() => import('~/pages/Main'));

const StoreConnections = React.lazy(() => import('~/pages/StoreConnections'));

const Login = React.lazy(() => import('~/pages/Login/LoginFormPage'));

const LoginSSO = React.lazy(() => import('~/pages/Login/LoginSSOFormPage'));

const EmailSent = React.lazy(() => import('~/pages/Login/EmailSentPage'));

const ForgotPassword = React.lazy(() => import('~/pages/Login/ForgotPasswordPage'));

const ResetPassword = React.lazy(() => import('~/pages/Auth/ResetPassword'));

const VerificationSuccess = React.lazy(() => import('~/pages/Auth/VerificationSuccess'));

const ResendVerification = React.lazy(() => import('~/pages/Auth/ResendVerification'));

const ResendVerificationSuccess = React.lazy(
  () => import('~/pages/Auth/ResendVerificationSuccess'),
);

const Questionnaire = React.lazy(() => import('~/pages/Auth/Questionnaire'));

const Root = React.lazy(() => import('~/pages/Root'));

const Onboarding = React.lazy(() => import('~/pages/Onboarding/Onboarding'));
const Onboarding2024 = React.lazy(() => import('~/pages/Onboarding2024/Onboarding2024'));

const Close = React.lazy(() => import('~/pages/Close'));

const routesWithoutSidebar: string[] = ['/orders/create', '/orders/:orderId'];
// we need to parse slugs because our router is overloading the :ordersId slug in our route declarations
const ordersSlugsWithSidebar: string[] = ['csv-import', 'batch'];

// ideally we would get the :orderId slug using react router but that value is not available in the react
// router params, possibly because we are overloading that slug value in our orders page route declarations
function getSlugFromPathname(pathname: string) {
  return pathname.split('/')[2];
}

// NOTE: the default for matchPath is exact:false, strict: false
// which means these matches will be more broad
function getShouldHideSidebar(pathname: string): boolean {
  const slug = getSlugFromPathname(pathname);
  const shouldSlugShowSidebar = ordersSlugsWithSidebar.includes(slug);
  // convert to boolean for easier/more explicit typing
  const isMatchedRoute = !!matchPath(pathname, {
    path: routesWithoutSidebar,
  });

  return isMatchedRoute && !shouldSlugShowSidebar;
}

export const PATHS = {
  close: '/close',
  join: '/join',
  questionnaire: '/questionnaire',
  challenge: '/challenge',
  login: '/login',
  ssoLogin: '/sso/login',
  sent: '/sent',
  forgotPassword: '/forgot_password',
  resetPassword: '/resetPassword',
  verificationSuccess: '/verification_success',
  connect: '/connect',
  resendVerification: '/resend_verification',
  resendVerificationSuccess: '/resend_verification/success',
  invoiceDisplay: '/settings/account/billing/invoices/:invoiceId',
  userBilling: '/user/billing',
  onboarding: '/onboarding',
  root: '/',
} as const;

const AppRoutes = () => {
  const { newJoinFlow } = useOnboarding2024();
  const renderMainLayout = React.useRef(({ location: { pathname } }: RouteComponentProps) => {
    return <Main shouldHideNav={getShouldHideSidebar(pathname)} />;
  }).current;

  return (
    <React.Suspense fallback="">
      <GlobalComponentsProvider>
        <Switch>
          <Route exact path={PATHS.close} component={Close} />
          <Route exact path={PATHS.join} component={newJoinFlow ? Register2024 : Register} />
          <Route exact path={PATHS.questionnaire} component={Questionnaire} />
          <Route exact path={[PATHS.challenge, PATHS.login]}>
            <Login />
          </Route>
          <Route exact path={[PATHS.ssoLogin]}>
            <LoginSSO />
          </Route>
          <Route exact path={PATHS.sent} component={EmailSent} />
          <Route exact path={PATHS.forgotPassword} component={ForgotPassword} />
          <Route exact path={PATHS.resetPassword} component={ResetPassword} />
          <Route exact path={PATHS.verificationSuccess} component={VerificationSuccess} />
          <AuthenticatedRoute path={PATHS.connect} component={StoreConnections} />
          <AuthenticatedRoute
            exact
            path={PATHS.resendVerification}
            component={ResendVerification}
          />
          <AuthenticatedRoute
            exact
            path={PATHS.resendVerificationSuccess}
            component={ResendVerificationSuccess}
          />
          <AuthenticatedRoute path={PATHS.invoiceDisplay} component={InvoiceDisplay} />
          <Redirect from={PATHS.userBilling} to="/settings/account/billing" />
          <AuthenticatedRoute
            path={PATHS.onboarding}
            component={newJoinFlow ? Onboarding2024 : Onboarding}
          />
          <AuthenticatedRoute exact path={PATHS.root} component={Root} />
          <Route render={renderMainLayout} />
        </Switch>
      </GlobalComponentsProvider>
    </React.Suspense>
  );
};

export default AppRoutes;
